import React, { useState, useEffect, useCallback } from 'react';
import { crearCampania, listarCampanias, eliminarCampania, subirPDFCampania } from '../../services/campanias';
import '../../styles/admin/AdminCampanias.css';

function AdminCampanias() {
  const [campanias, setCampanias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [nuevaCampaniaNombre, setNuevaCampaniaNombre] = useState('');
  const [archivoPDF, setArchivoPDF] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const cargarCampanias = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listarCampanias();
      setCampanias(data);
    } catch (err) {
      console.error('Error cargando campañas:', err);
      setError('Error al cargar las campañas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarCampanias();
  }, [cargarCampanias]);

  const handleCrearCampaniaConPDF = async (e) => {
    e.preventDefault();
    if (!nuevaCampaniaNombre.trim()) {
      setError('El nombre de la campaña es obligatorio');
      return;
    }
    if (!archivoPDF) {
      setError('Debes seleccionar un archivo PDF');
      return;
    }
    setUploading(true);
    setError('');
    try {
      // 1. Crear la campaña
      const nueva = await crearCampania({ nombre: nuevaCampaniaNombre });
      
      // 2. Subir y procesar el PDF
      const resultado = await subirPDFCampania(nueva.id, archivoPDF);
      
      alert(`✅ Campaña "${nueva.nombre}" creada exitosamente.\n${resultado.mensaje}\nPáginas: ${resultado.paginas}\nChunks generados: ${resultado.total_chunks}`);
      
      setShowModal(false);
      setNuevaCampaniaNombre('');
      setArchivoPDF(null);
      cargarCampanias();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta campaña? Se perderán todos los chunks y contenido asociado.')) return;
    try {
      await eliminarCampania(id);
      setCampanias(campanias.filter(c => c.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const getEstadoBadge = (estado) => {
    switch(estado) {
      case 'completado':
        return <span className="estado-badge completado">✅ Completado</span>;
      default:
        return <span className="estado-badge pendiente">⏳ Pendiente</span>;
    }
  };

  if (loading) {
    return <div className="admin-campanias-loading">Cargando campañas...</div>;
  }

  if (error && campanias.length === 0) {
    return (
      <div className="admin-campanias-error">
        <p>{error}</p>
        <button onClick={cargarCampanias}>Reintentar</button>
      </div>
    );
  }

  return (
    <div className="admin-campanias-container">
      <div className="admin-campanias-header">
        <h1>🚀 Campañas del Sistema</h1>
        <button className="btn-crear-campania" onClick={() => setShowModal(true)}>
          + Nueva Campaña
        </button>
      </div>

      {/* ESTADÍSTICAS RÁPIDAS */}
      <div className="admin-campanias-stats">
        <div className="stat-card">
          <span className="stat-valor">{campanias.length}</span>
          <span className="stat-label">Total campañas</span>
        </div>
        <div className="stat-card">
          <span className="stat-valor">{campanias.filter(c => c.estado === 'completado').length}</span>
          <span className="stat-label">Completadas</span>
        </div>
        <div className="stat-card">
          <span className="stat-valor">{campanias.filter(c => c.estado === 'pendiente').length}</span>
          <span className="stat-label">Pendientes</span>
        </div>
      </div>

      {/* TABLA DE CAMPAÑAS */}
      {campanias.length === 0 ? (
        <div className="admin-campanias-empty">
          <p>No hay campañas en el sistema. Crea la primera campaña.</p>
          <button className="btn-crear-primera" onClick={() => setShowModal(true)}>
            + Crear primera campaña
          </button>
        </div>
      ) : (
        <div className="admin-campanias-table-wrapper">
          <table className="admin-campanias-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>PDF</th>
                <th>Fecha</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {campanias.map((campania) => (
                <tr key={campania.id}>
                  <td className="nombre-col">{campania.nombre}</td>
                  <td className="pdf-col">
                    {campania.pdf_original_nombre ? (
                      <span className="pdf-nombre" title={campania.pdf_original_nombre}>
                        📄 {campania.pdf_original_nombre.length > 30 ? campania.pdf_original_nombre.substring(0, 30) + '...' : campania.pdf_original_nombre}
                      </span>
                    ) : (
                      <span className="sin-pdf">Sin PDF</span>
                    )}
                  </td>
                  <td>{new Date(campania.fecha_creacion).toLocaleDateString()}</td>
                  <td>{getEstadoBadge(campania.estado)}</td>
                  <td className="acciones-col">
                    <button className="btn-eliminar" onClick={() => handleEliminar(campania.id)}>
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL CREAR CAMPAÑA CON PDF */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nueva Campaña</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleCrearCampaniaConPDF}>
              <div className="modal-field">
                <label>Nombre de la campaña *</label>
                <input
                  type="text"
                  value={nuevaCampaniaNombre}
                  onChange={(e) => setNuevaCampaniaNombre(e.target.value)}
                  required
                  placeholder="Ej: Lanzamiento Producto X"
                  autoFocus
                  disabled={uploading}
                />
              </div>
              <div className="modal-field">
                <label>Archivo PDF *</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setArchivoPDF(e.target.files[0])}
                  required
                  disabled={uploading}
                />
                <small>El PDF será procesado para extraer el contenido y generar embeddings. (Sin límites para administradores)</small>
              </div>
              {error && <div className="modal-error">{error}</div>}
              <button type="submit" className="modal-btn" disabled={uploading}>
                {uploading ? 'Creando y procesando...' : 'Crear campaña y subir PDF'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCampanias;